import React from "react";
import {
  Box,
  Text,
  Table,
  Badge,
  Skeleton,
  createStyles,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";

const useStyles = createStyles(() => ({
  container: {
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "20px",
    border: "1px solid #EAECF0",
  },
  headerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#344054",
  },
  table: {
    "& thead th": {
      fontSize: 12,
      fontWeight: 500,
      fontFamily: "Inter",
      color: "#667085",
      backgroundColor: "#F9FAFB",
      borderBottom: "1px solid #EAECF0",
      padding: "8px 12px",
    },
    "& tbody td": {
      fontSize: 13,
      fontWeight: 500,
      fontFamily: "Inter",
      color: "#344054",
      padding: "10px 12px",
      borderBottom: "1px solid #EAECF0",
    },
    "& tbody tr:last-of-type td": {
      borderBottom: "none",
    },
  },
  providerName: {
    fontSize: 13,
    fontWeight: 600,
    fontFamily: "Inter",
    color: "#101828",
  },
  scoreBadge: {
    fontWeight: 600,
    fontFamily: "Inter",
    fontSize: 12,
    minWidth: 48,
    height: 24,
  },
  auditFlag: {
    fontWeight: 600,
    fontFamily: "Inter",
    fontSize: 11,
    height: 22,
  },
  emptyState: {
    padding: "40px 0",
    textAlign: "center",
    color: "#667085",
    fontSize: 13,
    fontFamily: "Inter",
  },
}));

const getBandColor = (band) => {
  switch (band) {
    case "red":
      return { bg: "#FEF3F2", color: "#F04438" };
    case "yellow":
      return { bg: "#FFF6ED", color: "#F79009" };
    case "green":
      return { bg: "#ECFDF3", color: "#12B76A" };
    default:
      return { bg: "#F2F4F7", color: "#667085" };
  }
};

const HighRiskProviders = ({ data, loading, onProviderClick }) => {
  const { classes } = useStyles();

  if (loading) {
    return (
      <Box className={classes.container}>
        <Skeleton height={14} width={200} mb={16} />
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} height={40} mb={8} />
        ))}
      </Box>
    );
  }

  const providers = data || [];

  return (
    <Box className={classes.container}>
      <Box className={classes.headerRow}>
        <Text className={classes.title}>High-Risk Providers</Text>
        {providers.length > 0 && (
          <Badge
            color="red"
            variant="light"
            size="sm"
            leftSection={<IconAlertTriangle size={12} />}
          >
            {providers.length} requiring attention
          </Badge>
        )}
      </Box>

      {providers.length === 0 ? (
        <Text className={classes.emptyState}>
          No high-risk providers detected
        </Text>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
        <Table className={classes.table}>
          <thead>
            <tr>
              <th>Provider</th>
              <th>Score</th>
              <th>Overdue Notes</th>
              <th>Expiring Docs</th>
              <th>Missed MARs</th>
              <th>Overdue Tasks</th>
              <th>Audit</th>
            </tr>
          </thead>
          <tbody>
            {providers.map((provider) => {
              const bandColor = getBandColor(provider.band);
              return (
                <tr
                  key={provider.providerId}
                  style={{ cursor: "pointer" }}
                  onClick={() => onProviderClick?.(provider.providerId)}
                >
                  <td>
                    <Text className={classes.providerName}>
                      {provider.name}
                    </Text>
                  </td>
                  <td>
                    <Badge
                      className={classes.scoreBadge}
                      sx={{
                        backgroundColor: bandColor.bg,
                        color: bandColor.color,
                      }}
                    >
                      {provider.score}
                    </Badge>
                  </td>
                  <td>{provider.overdueNotes ?? 0}</td>
                  <td>{provider.expiringDocs ?? 0}</td>
                  <td>{provider.missedMARs ?? 0}</td>
                  <td>{provider.overdueTasks ?? 0}</td>
                  <td>
                    {provider.auditFlag ? (
                      <Badge
                        className={classes.auditFlag}
                        sx={{
                          backgroundColor: "#FEF3F2",
                          color: "#F04438",
                        }}
                      >
                        FLAG
                      </Badge>
                    ) : (
                      <Text size="xs" color="dimmed">
                        --
                      </Text>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
        </Box>
      )}
    </Box>
  );
};

export default HighRiskProviders;
